import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Fab,
  Tooltip,
  Alert,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Tab,
  Tabs
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  VideoLibrary as VideoLibraryIcon,
  Link as LinkIcon
} from '@mui/icons-material';

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  featuredImage?: string;
  images: string[];
  youtubeLinks: string[];
  author: string;
  authorName: string;
  category: string;
  tags: string[];
  status: 'draft' | 'published' | 'archived';
  isFeatured: boolean;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const BlogPostsManagementPage: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    images: [] as string[],
    youtubeLinks: [] as string[],
    category: 'General',
    tags: [] as string[],
    status: 'draft' as 'draft' | 'published' | 'archived',
    isFeatured: false
  });

  const [tagInput, setTagInput] = useState('');
  const [youtubeInput, setYoutubeInput] = useState('');
  const [imageInput, setImageInput] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFeatured, setUploadingFeatured] = useState(false);

  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/blog-posts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setBlogPosts(data.data || []);
      }
    } catch (error) {
      console.error('Error loading blog posts:', error);
      showSnackbar('Failed to load blog posts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDialogOpen = (post?: BlogPost) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title,
        content: post.content,
        excerpt: post.excerpt,
        featuredImage: post.featuredImage || '',
        images: post.images || [],
        youtubeLinks: post.youtubeLinks || [],
        category: post.category,
        tags: post.tags || [],
        status: post.status,
        isFeatured: post.isFeatured
      });
    } else {
      setEditingPost(null);
      setFormData({
        title: '',
        content: '',
        excerpt: '',
        featuredImage: '',
        images: [],
        youtubeLinks: [],
        category: 'General',
        tags: [],
        status: 'draft',
        isFeatured: false
      });
    }
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      featuredImage: '',
      images: [],
      youtubeLinks: [],
      category: 'General',
      tags: [],
      status: 'draft',
      isFeatured: false
    });
    setTagInput('');
    setYoutubeInput('');
    setImageInput('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddImage = (imageUrl: string) => {
    if (imageUrl.trim() && !formData.images.includes(imageUrl.trim())) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, imageUrl.trim()]
      }));
    }
  };

  const handleRemoveImage = (imageToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== imageToRemove)
    }));
  };

  const handleUploadImage = async (file: File, type: 'featured' | 'gallery') => {
    if (!file) return;

    const setUploading = type === 'featured' ? setUploadingFeatured : setUploadingImage;
    setUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      const data = await response.json();

      // Backend returns { success: true, data: { url: ... } }
      const imageUrl = data.data?.url || data.url;
      if (data.success && imageUrl) {
        if (type === 'featured') {
          setFormData(prev => ({ ...prev, featuredImage: imageUrl }));
        } else {
          setFormData(prev => ({ ...prev, images: [...prev.images, imageUrl] }));
        }
        showSnackbar('Image uploaded successfully', 'success');
      } else {
        showSnackbar(data.message || 'Failed to upload image', 'error');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showSnackbar('Failed to upload image', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAddYoutubeLink = () => {
    const url = youtubeInput.trim();
    if (!url) return;

    // Validate YouTube URL and extract video ID
    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/;
    const match = url.match(youtubeRegex);

    if (!match) {
      showSnackbar('Invalid YouTube URL', 'error');
      return;
    }

    const videoId = match[1];
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;

    if (!formData.youtubeLinks.includes(embedUrl)) {
      setFormData(prev => ({
        ...prev,
        youtubeLinks: [...prev.youtubeLinks, embedUrl]
      }));
      setYoutubeInput('');
      showSnackbar('YouTube link added', 'success');
    } else {
      showSnackbar('This video is already added', 'error');
    }
  };

  const handleRemoveYoutubeLink = (linkToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      youtubeLinks: prev.youtubeLinks.filter(link => link !== linkToRemove)
    }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.title.trim()) {
      showSnackbar('Title is required', 'error');
      return;
    }
    if (!formData.excerpt.trim()) {
      showSnackbar('Excerpt is required', 'error');
      return;
    }
    if (!formData.content.trim()) {
      showSnackbar('Content is required', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingPost
        ? `${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/blog-posts/${editingPost._id}`
        : `${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/blog-posts`;
      
      const method = editingPost ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        showSnackbar(editingPost ? 'Blog post updated successfully' : 'Blog post created successfully', 'success');
        handleDialogClose();
        loadBlogPosts();
      } else {
        showSnackbar(data.message || 'Failed to save blog post', 'error');
      }
    } catch (error) {
      console.error('Error saving blog post:', error);
      showSnackbar('Failed to save blog post', 'error');
    }
  };

  const handleDelete = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/blog-posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        showSnackbar('Blog post deleted successfully', 'success');
        loadBlogPosts();
      } else {
        showSnackbar('Failed to delete blog post', 'error');
      }
    } catch (error) {
      console.error('Error deleting blog post:', error);
      showSnackbar('Failed to delete blog post', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredPosts = blogPosts.filter(post => {
    if (tabValue === 0) return true; // All
    if (tabValue === 1) return post.status === 'published';
    if (tabValue === 2) return post.status === 'draft';
    if (tabValue === 3) return post.isFeatured;
    return true;
  });

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography>Loading blog posts...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">
          Blog Posts Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleDialogOpen()}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #653a91 100%)',
            }
          }}
        >
          Add Blog Post
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`All Posts (${blogPosts.length})`} />
          <Tab label={`Published (${blogPosts.filter(p => p.status === 'published').length})`} />
          <Tab label={`Drafts (${blogPosts.filter(p => p.status === 'draft').length})`} />
          <Tab label={`Featured (${blogPosts.filter(p => p.isFeatured).length})`} />
        </Tabs>
      </Paper>

      <Grid container spacing={3}>
        {filteredPosts.map((post) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={post._id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {post.featuredImage && (
                <CardMedia
                  component="img"
                  height="200"
                  image={post.featuredImage}
                  alt={post.title}
                />
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Chip
                    label={post.status}
                    size="small"
                    color={post.status === 'published' ? 'success' : post.status === 'draft' ? 'warning' : 'default'}
                  />
                  {post.isFeatured && (
                    <Chip label="Featured" size="small" color="primary" />
                  )}
                </Box>
                <Typography variant="h6" gutterBottom noWrap>
                  {post.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {post.excerpt}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {post.tags.slice(0, 3).map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    By {post.authorName || 'Unknown'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {new Date(post.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
              <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => handleDialogOpen(post)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(post._id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPost ? 'Edit Blog Post' : 'Add New Blog Post'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {editingPost && (
              <TextField
                label="Author"
                fullWidth
                value={editingPost.authorName || 'Unknown'}
                disabled
                InputProps={{ readOnly: true }}
                helperText="Author is automatically set based on logged-in user"
              />
            )}
            <TextField
              name="title"
              label="Title"
              fullWidth
              value={formData.title}
              onChange={handleInputChange}
              required
            />

            <TextField
              name="excerpt"
              label="Excerpt"
              fullWidth
              multiline
              rows={2}
              value={formData.excerpt}
              onChange={handleInputChange}
              required
            />

            <TextField
              name="content"
              label="Content"
              fullWidth
              multiline
              rows={10}
              value={formData.content}
              onChange={handleInputChange}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                label="Category"
              >
                <MenuItem value="General">General</MenuItem>
                <MenuItem value="Events">Events</MenuItem>
                <MenuItem value="News">News</MenuItem>
                <MenuItem value="Education">Education</MenuItem>
                <MenuItem value="Career">Career</MenuItem>
                <MenuItem value="Technology">Technology</MenuItem>
                <MenuItem value="Business">Business</MenuItem>
              </Select>
            </FormControl>

            {/* Featured Image Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Featured Image</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploadingFeatured}
                >
                  {uploadingFeatured ? 'Uploading...' : 'Upload Image'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUploadImage(file, 'featured');
                      e.target.value = '';
                    }}
                  />
                </Button>
                <TextField
                  size="small"
                  placeholder="Or paste image URL"
                  value={formData.featuredImage}
                  onChange={(e) => setFormData(prev => ({ ...prev, featuredImage: e.target.value }))}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </Box>
              {formData.featuredImage && (
                <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                  <img
                    src={formData.featuredImage}
                    alt="Featured"
                    style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setFormData(prev => ({ ...prev, featuredImage: '' }))}
                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/* Gallery Images */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Gallery Images</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? 'Uploading...' : 'Upload Images'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      files.forEach(file => handleUploadImage(file, 'gallery'));
                      e.target.value = '';
                    }}
                  />
                </Button>
                <TextField
                  size="small"
                  placeholder="Or paste image URL"
                  value={imageInput}
                  onChange={(e) => setImageInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (handleAddImage(imageInput), setImageInput(''))}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <Button variant="outlined" onClick={() => { handleAddImage(imageInput); setImageInput(''); }}>
                  Add URL
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.images.map((img) => (
                  <Box key={img} sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={img}
                      alt="Gallery"
                      style={{ width: 100, height: 100, borderRadius: 8, objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveImage(img)}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* YouTube Links */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VideoLibraryIcon fontSize="small" />
                YouTube Videos
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Paste YouTube URL (e.g., https://youtube.com/watch?v=...)"
                  value={youtubeInput}
                  onChange={(e) => setYoutubeInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddYoutubeLink()}
                  fullWidth
                />
                <Button variant="outlined" onClick={handleAddYoutubeLink} startIcon={<LinkIcon />}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {formData.youtubeLinks.map((link) => (
                  <Box key={link} sx={{ position: 'relative', flex: '1 1 calc(50% - 8px)', minWidth: 300 }}>
                    <iframe
                      width="100%"
                      height="200"
                      src={link}
                      title="YouTube video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ borderRadius: 8 }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveYoutubeLink(link)}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Tags</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Add tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  fullWidth
                />
                <Button variant="outlined" onClick={handleAddTag}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => handleRemoveTag(tag)}
                  />
                ))}
              </Box>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                label="Status"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isFeatured}
                  onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                />
              }
              label="Featured Post"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPost ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BlogPostsManagementPage;
